import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, media } from "@/lib/api";
import { Package, ArrowRight } from "lucide-react";

export default function Cases() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products", { params: { product_type: "case" } })
      .then(({ data }) => setCases(data))
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto" data-testid="cases-page">
      <div className="mono-label mb-3">Shop / Storage & Accessories</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-white mb-3">Peptide Cases</h1>
      <p className="text-slate-400 text-sm max-w-2xl mb-10">
        Pharmacist-designed vial storage cases — keep your peptides and bac water safe, organized and cold-chain ready. Fridge and freezer safe, built from durable impact-resistant materials.
      </p>

      {loading && <div className="text-slate-500 font-mono text-sm animate-pulse">Loading cases…</div>}
      {!loading && cases.length === 0 && (
        <div className="glass rounded-2xl p-12 text-center text-slate-500 text-sm" data-testid="cases-empty">No cases available yet.</div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="cases-grid">
        {cases.map((p) => (
          <Link key={p.id} to={`/shop/${p.slug}`} data-testid={`case-card-${p.slug}`}
            className="glass rounded-3xl overflow-hidden group hover:border-violet-400/40 transition-all duration-300 flex flex-col">
            <div className="aspect-[4/3] bg-black/40 overflow-hidden relative">
              {p.image ? (
                <img src={media(p.image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Package className="text-violet-300" size={32} /></div>
              )}
              <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 border border-violet-400/40 text-[10px] font-mono uppercase tracking-widest text-violet-200">Case</span>
            </div>
            <div className="p-6 flex flex-col flex-1">
              <div className="font-serif text-xl text-white">{p.name.replace("CG Labz ", "")}</div>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed line-clamp-2">{p.tagline}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {p.vial_capacity != null && (
                  <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-mono text-cyan-300">{p.vial_capacity} vials</span>
                )}
                {p.fits_vials && (
                  <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-mono text-slate-300">Fits {p.fits_vials.split("(")[0].trim()}</span>
                )}
              </div>

              <div className="mt-auto pt-5 flex items-center justify-between">
                {p.price != null && (
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-mono text-white">${p.price.toFixed(2)}</div>
                    {p.compare_at_price > p.price && <div className="text-xs font-mono text-slate-500 line-through">${p.compare_at_price.toFixed(2)}</div>}
                  </div>
                )}
                <span className="inline-flex items-center gap-1.5 text-xs font-mono text-violet-300 group-hover:text-violet-200">
                  View case <ArrowRight size={13} />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
